import { useMemo, useState } from 'react';
import { getRecentAudit, relativeTimeBR } from '@/lib/audit';
import { ScrollText, Plus, Pencil, Trash2, Smartphone, Filter, X } from 'lucide-react';
import { useVisiblePolling } from '@/hooks/useVisiblePolling';

const ACTION_META = {
  create: { label: 'criou', Icon: Plus, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  update: { label: 'atualizou', Icon: Pencil, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  delete: { label: 'removeu', Icon: Trash2, color: 'bg-red-100 text-red-700 border-red-200' },
};

const ALL = '__all__';
const UNNAMED = '__unnamed__';

function deviceKey(entry) {
  if (entry.deviceNickname) return entry.deviceNickname;
  if (entry.deviceUid) return `UID:${entry.deviceUid}`;
  return UNNAMED;
}

function deviceLabel(key) {
  if (key === UNNAMED) return 'Aparelhos sem apelido';
  if (key.startsWith('UID:')) return `UID ${key.slice(4, 10)}…`;
  return key;
}

function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear()
    && d.getMonth() === n.getMonth()
    && d.getDate() === n.getDate();
}

export default function AuditLogPanel({ limit = 50 }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deviceFilter, setDeviceFilter] = useState(ALL);
  const [todayOnly, setTodayOnly] = useState(false);

  const load = async () => {
    // Lemos mais itens que o limite visível para que o filtro tenha "material".
    const rows = await getRecentAudit(Math.max(limit * 3, 150));
    setEntries(rows);
    setLoading(false);
  };

  useVisiblePolling(load, 10000);

  const deviceOptions = useMemo(() => {
    const set = new Map();
    entries.forEach((e) => {
      const k = deviceKey(e);
      if (!set.has(k)) set.set(k, deviceLabel(k));
    });
    return Array.from(set.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [entries]);

  const filtered = useMemo(() => {
    let rows = entries;
    if (deviceFilter !== ALL) rows = rows.filter((e) => deviceKey(e) === deviceFilter);
    if (todayOnly) rows = rows.filter((e) => isToday(e.at));
    return rows.slice(0, limit);
  }, [entries, deviceFilter, todayOnly, limit]);

  const clearFilters = () => {
    setDeviceFilter(ALL);
    setTodayOnly(false);
  };

  const hasFilter = deviceFilter !== ALL || todayOnly;

  return (
    <section data-testid="audit-log-panel" className="bg-white border border-slate-200 rounded-2xl p-5">
      <header className="flex items-start gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-800 to-slate-600 text-white flex items-center justify-center shrink-0">
          <ScrollText size={14} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-slate-900 text-sm">Log de Auditoria</p>
          <p className="text-[11px] text-slate-500">
            {hasFilter
              ? `Exibindo ${filtered.length} ação${filtered.length === 1 ? '' : 'ões'} filtrada${filtered.length === 1 ? '' : 's'}.`
              : `Últimas ${filtered.length} ações em Comandas, Delivery e Cozinha — quem, quando, o quê.`}
          </p>
        </div>
      </header>

      {/* === Filtros === */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-500 shrink-0">
          <Filter size={11} /> Filtrar
        </div>

        <div className="flex items-center gap-1.5">
          <Smartphone size={12} className="text-slate-400" />
          <select
            data-testid="audit-device-filter"
            value={deviceFilter}
            onChange={(e) => setDeviceFilter(e.target.value)}
            className="h-8 text-xs font-semibold border-2 border-slate-200 rounded-lg px-2 bg-white focus:border-slate-400 focus:outline-none max-w-[200px] truncate"
          >
            <option value={ALL}>Todos os aparelhos</option>
            {deviceOptions.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <label
          data-testid="audit-today-toggle"
          className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border-2 text-xs font-bold cursor-pointer transition-colors ${
            todayOnly
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
          }`}
        >
          <input
            type="checkbox"
            checked={todayOnly}
            onChange={(e) => setTodayOnly(e.target.checked)}
            className="sr-only"
          />
          Hoje
        </label>

        {hasFilter && (
          <button
            data-testid="audit-clear-filter"
            onClick={clearFilters}
            className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[11px] font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            <X size={11} /> Limpar
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-center text-sm text-slate-400 py-6">Carregando...</p>
      ) : filtered.length === 0 ? (
        <div data-testid="audit-empty" className="text-center py-8">
          <ScrollText size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
          <p className="text-sm font-semibold text-slate-500">
            {hasFilter ? 'Nenhuma ação com esses filtros' : 'Nenhuma ação registrada ainda'}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {hasFilter
              ? 'Tente "Todos os aparelhos" ou desative "Hoje".'
              : 'Assim que alguém abrir/fechar comandas ou preparar pedidos, elas aparecem aqui.'}
          </p>
        </div>
      ) : (
        <ul data-testid="audit-list" className="space-y-2">
          {filtered.map((e) => {
            const meta = ACTION_META[e.action] || { label: e.action, Icon: ScrollText, color: 'bg-slate-100 text-slate-700 border-slate-200' };
            const Icon = meta.Icon;
            const device = e.deviceNickname || (e.deviceUid ? `UID ${e.deviceUid.slice(0, 6)}…` : 'Aparelho sem nome');
            return (
              <li
                key={e.id}
                data-testid={`audit-item-${e.id}`}
                className="flex items-start gap-3 bg-slate-50/60 border border-slate-100 rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors"
              >
                <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon size={13} strokeWidth={2.25} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">
                    <Smartphone size={11} className="inline mr-1 -mt-0.5 text-slate-400" strokeWidth={2} />
                    <span data-testid={`audit-item-device-${e.id}`} className="text-slate-700">{device}</span>
                    <span className="text-slate-400 mx-1">·</span>
                    <span className="text-slate-900">{e.summary}</span>
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{relativeTimeBR(e.at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
